import React from 'react';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup';
import { DaycareDurationOption, LatePickupFeeType, LATE_PICKUP_FEE_TYPES } from 'model/Service';
import {
    getLatePickupFeeOptionsForDuration,
    RadioGroupLatePickupOption
} from 'components/Daycare/Form/utils/DaycarePricingUtils';
import styles from './LatePickupFeeConfig.module.scss';

interface LatePickupFeeConfigProps {
    durationOption: DaycareDurationOption | null;
    latePickupFee: {
        enabled: boolean;
        type?: LatePickupFeeType;
        gracePeriod: number;
    };
    onLatePickupFeeChange: (config: { enabled: boolean; type?: LatePickupFeeType; gracePeriod: number }) => void;
    className?: string;
}

export const LatePickupFeeConfig: React.FC<LatePickupFeeConfigProps> = ({
    durationOption,
    latePickupFee,
    onLatePickupFeeChange,
    className
}) => {
    // Generate late pickup fee options based on duration using centralized helper
    const getLatePickupOptions = (): RadioGroupLatePickupOption[] => {
        if (!durationOption) return [];

        return getLatePickupFeeOptionsForDuration(durationOption).map(option => ({
            value: option.value,
            inlineLabel: option.label,
            description: undefined
        }));
    };

    const handleSwitchChange = (enabled: boolean) => {
        // Always use 'per_hour' as default regardless of duration
        const defaultType = LATE_PICKUP_FEE_TYPES.PER_HOUR;

        onLatePickupFeeChange({
            enabled,
            type: enabled ? defaultType : undefined,
            gracePeriod: latePickupFee.gracePeriod
        });
    };

    const handleTypeChange = (type: LatePickupFeeType) => {
        onLatePickupFeeChange({
            ...latePickupFee,
            type
        });
    };

    const handleGracePeriodChange = (gracePeriod: number) => {
        onLatePickupFeeChange({
            ...latePickupFee,
            gracePeriod
        });
    };

    const options = getLatePickupOptions();

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.switchSection}>
                <span className={styles.switchLabel}>Late Pickup Fee</span>
                <Switch
                    checked={latePickupFee.enabled}
                    onChange={handleSwitchChange}
                />
            </div>

            {latePickupFee.enabled && (
                <div className={styles.optionsSection}>
                    <div className={styles.radioGrid}>
                        <RadioGroup<LatePickupFeeType>
                            className={styles.radioGroup}
                            options={options}
                            value={latePickupFee.type || LATE_PICKUP_FEE_TYPES.PER_HOUR}
                            onChange={handleTypeChange}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
